import { useState } from 'react'
import Drawer from '../Drawer'
import { Notice } from '../Toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getBands, setBands } from '../api/endpoints'
import { ApiError } from '../api/client'
import type { Band } from '../api/types'
import { ErrorNotice } from '../components'

/** Edits the admission bands for a program, a campaign, or one program in one campaign. Ranges are inclusive and must not overlap. */
export default function BandsDialog({ title, programId, cycleId, onClose }: { title: string; programId: number | null; cycleId: number | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const loaded = useQuery({ queryKey: ['bands', programId, cycleId], queryFn: () => getBands({ programId, cycleId }) })
  const [rows, setRows] = useState<Band[] | null>(null)
  const current = rows ?? loaded.data ?? []
  const save = useMutation({
    mutationFn: () => setBands({ programId, cycleId, bands: current.map((b) => ({ label: b.label, minScore: Number(b.minScore), maxScore: Number(b.maxScore) })) }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['bands'] }); onClose() },
  })
  const change = (i: number, patch: Partial<Band>) => setRows(current.map((b, j) => (j === i ? { ...b, ...patch } : b)))
  const fieldError = save.error instanceof ApiError ? save.error.fields.bands : undefined
  return (
    <Drawer title={title} width={620} onClose={onClose} onSubmit={() => { save.mutate() }}
      footer={<><button type="button" onClick={onClose}>Cancel</button><button type="submit" className="primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save bands'}</button></>}>
        <p className="muted">Name each range of composite score, for example “Strong fit” from 40 to 60. The report shows the band to the admission panel only.</p>
        <ErrorNotice error={loaded.error} />
        {current.map((b, i) => (
          <div className="row" key={i} style={{ marginBottom: 8 }}>
            <input aria-label={`Band ${i + 1} label`} placeholder="Label" value={b.label} onChange={(e) => change(i, { label: e.target.value })} style={{ flex: 2, minWidth: 140 }} />
            <input aria-label={`Band ${i + 1} lowest score`} type="number" step="0.1" value={b.minScore} onChange={(e) => change(i, { minScore: e.target.value as unknown as number })} style={{ width: 90 }} />
            <span>to</span>
            <input aria-label={`Band ${i + 1} highest score`} type="number" step="0.1" value={b.maxScore} onChange={(e) => change(i, { maxScore: e.target.value as unknown as number })} style={{ width: 90 }} />
            <button type="button" aria-label={`Remove band ${i + 1}`} onClick={() => setRows(current.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        {fieldError && <Notice tone="error">{fieldError}</Notice>}
        <ErrorNotice error={fieldError ? null : save.error} />
        <button type="button" onClick={() => setRows([...current, { label: '', minScore: 0, maxScore: 0 }])}>Add a band</button>
    </Drawer>
  )
}
